import React from 'react';
import styled from '@emotion/styled';
import { CustomCard, AddCircle, ActionButtonProps } from '@innovationdepartment/proxima-ui';
import { PillProps } from '@innovationdepartment/proxima-ui/dist/esm/types/pill';
import OnboardingNavbar from './Onboarding.Navbar';
import OnboardingMainSection from './Onboarding.MainSection';
import OnboardingBrands from './Onboarding.Brands';

const OnboardingWrapper = styled.div`
  margin-top: 72px;
  padding: 48px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

type IntegrationItem = {
  icon: JSX.Element;
  type?: string;
  title: React.ReactNode;
  description: string;
  onClick: () => void;
  pill?: PillProps;
  loading: boolean;
  isRequired?: boolean;
  isConnected: boolean;
  isUnavailable?: boolean;
  excludeIntegrationStatus?: boolean;
};

type OnboardingViewProps = {
  integrationsItems: IntegrationItem[];
  onContinue: () => void;
  onSkip: () => void;
};

type ActionButtonContentProps = {
  excludeIntegrationStatus: boolean | undefined;
  isConnected: boolean;
};

const OnboardingView = (props: OnboardingViewProps) => {
  const { integrationsItems, onSkip, onContinue } = props;

  const skip = integrationsItems.every(
    (integration) => !(integration.isRequired && integration.isConnected)
  );

  const isConnectedLabel = (isConnected: boolean) => (isConnected ? 'Connected' : 'Connect');
  const isConnectedIcon = (isConnected: boolean) => (isConnected ? <></> : <AddCircle />);

  const actionButtonLabel = ({ excludeIntegrationStatus, isConnected }: ActionButtonContentProps) =>
    excludeIntegrationStatus ? 'Go Now' : isConnectedLabel(isConnected);

  const actionIcon = ({ excludeIntegrationStatus, isConnected }: ActionButtonContentProps) =>
    excludeIntegrationStatus ? <></> : isConnectedIcon(isConnected);

  return (
    <>
      <OnboardingNavbar skip={skip} onSkip={onSkip} onContinue={onContinue} />
      <OnboardingWrapper>
        <OnboardingMainSection>
          {integrationsItems.map((integration) => {
            const {
              icon,
              title,
              description,
              onClick,
              pill,
              loading,
              isConnected,
              isUnavailable,
              excludeIntegrationStatus,
            } = integration;

            const actionButton: ActionButtonProps | undefined = isUnavailable
              ? undefined
              : {
                  onClick: isConnected ? undefined : onClick,
                  label: actionButtonLabel({ excludeIntegrationStatus, isConnected }),
                  loading,
                  icon: actionIcon({ excludeIntegrationStatus, isConnected }),
                  variant: isConnected ? 'success' : 'default',
                };

            return (
              <CustomCard
                headerProps={{
                  icon,
                  pill,
                  actionButton,
                }}
                key={description}
                label={title}
                bodyContent={description}
                size="medium"
              />
            );
          })}
        </OnboardingMainSection>
        <OnboardingBrands />
      </OnboardingWrapper>
    </>
  );
};

export default OnboardingView;
